// src/api.js
import axios from 'axios';
import moment from 'moment'; // You might need to add moment.js to your project for this

// Simple in-memory cache for API responses
const apiCache = {
    games: null,
    predictions: null,
    eventStats: {},
    eventLineups: {},
    boxScores: {},
    liveScores: null,
    liveGames: {},
    cacheTime: 5 * 60 * 1000, // 5 minutes cache time for most data
    gamesCacheTime: 30 * 60 * 1000, // 30 minutes for games (they change less frequently)
    liveScoresCacheTime: 30 * 1000, // 30 seconds for live scores
    lastFetch: {},
    isFetchingGames: false // Prevent concurrent fetches
};

// Function to check if cache is valid
const isCacheValid = (cacheKey) => {
    const lastFetch = apiCache.lastFetch[cacheKey];
    if (!lastFetch) return false;
    
    // Use different cache times for different data types
    let cacheTime = apiCache.cacheTime;
    if (cacheKey === 'games') {
        cacheTime = apiCache.gamesCacheTime;
    } else if (cacheKey.startsWith('live_scores') || cacheKey.startsWith('live_game')) {
        cacheTime = apiCache.liveScoresCacheTime;
    }
    
    return Date.now() - lastFetch < cacheTime;
};

// Function to clear cache
export const clearApiCache = () => {
    apiCache.games = null;
    apiCache.predictions = null;
    apiCache.eventStats = {};
    apiCache.eventLineups = {};
    apiCache.boxScores = {};
    apiCache.liveScores = null;
    apiCache.liveGames = {};
    apiCache.lastFetch = {};
    console.log('API cache cleared');
};

// Function to dynamically determine API URL
const getApiBaseUrl = () => {
    // First check for explicit environment variable
    if (process.env.REACT_APP_API_BASE_URL) {
        return process.env.REACT_APP_API_BASE_URL;
    }
    
    // Local development
    if (process.env.NODE_ENV === 'development') {
        return 'http://localhost:8000';
    }
    
    // Production/Preview fallback - should be set via environment variable
    // This fallback should only be used if REACT_APP_API_BASE_URL is not configured
    return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
};

// Set up the base URL for your Go backend server
const API_BASE_URL = getApiBaseUrl();

// NBA API (FastAPI server) - Python API on port 8005
const getNBAApiBaseUrl = () => {
    if (process.env.REACT_APP_NBA_API_BASE_URL) {
        return process.env.REACT_APP_NBA_API_BASE_URL;
    }
    
    if (process.env.NODE_ENV === 'production') {
        throw new Error('REACT_APP_NBA_API_BASE_URL must be set for production builds');
    }
    
    // Local development - Python FastAPI server
    return 'http://localhost:8005';
};

const NBA_API_BASE_URL = getNBAApiBaseUrl();

// API configuration completed
console.log(`🔧 API Configuration:`, {
    goServer: API_BASE_URL,
    pythonServer: NBA_API_BASE_URL,
    env: process.env.NODE_ENV,
    timestamp: new Date().toISOString()
});

// Enhanced logging utilities
const Logger = {
    info: (context, message, data = {}) => {
        console.log(`🔍 [${context}] ${message}`, {
            timestamp: new Date().toISOString(),
            context,
            ...data
        });
    },
    error: (context, message, error = null, data = {}) => {
        console.error(`❌ [${context}] ${message}`, {
            timestamp: new Date().toISOString(),
            context,
            error: error?.message || error,
            stack: error?.stack,
            ...data
        });
    },
    warn: (context, message, data = {}) => {
        console.warn(`⚠️ [${context}] ${message}`, {
            timestamp: new Date().toISOString(),
            context,
            ...data
        });
    },
    debug: (context, message, data = {}) => {
        if (process.env.NODE_ENV === 'development') {
            console.debug(`🐛 [${context}] ${message}`, {
                timestamp: new Date().toISOString(),
                context,
                ...data
            });
        }
    }
};

// Generate correlation ID for request tracing
const generateCorrelationId = () => {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const dummyPlayers = [
    {
        "name": "LeBron James",
        "team": "Los Angeles Lakers",
        "nationality": "United States",
        "position": "Small Forward",
        "date_of_birth": "1984-12-30"
    },
    {
        "name": "Darvin Ham",
        "team": "Los Angeles Lakers",
        "nationality": "United States",
        "position": "Manager",
        "date_of_birth": "1973-07-23"
    },
    {
        "name": "D'Angelo Russell",
        "team": "Los Angeles Lakers",
        "nationality": "United States",
        "position": "Point Guard",
        "date_of_birth": "1996-02-23"
    },
    {
        "name": "Anthony Davis",
        "team": "Los Angeles Lakers",
        "nationality": "United States",
        "position": "Power Forward",
        "date_of_birth": "1993-03-11"
    },
    {
        "name": "Colin Castleton",
        "team": "Los Angeles Lakers",
        "nationality": "United States",
        "position": "Center",
        "date_of_birth": "2000-05-25"
    },
    {
        "name": "Max Christie",
        "team": "Los Angeles Lakers",
        "nationality": "United States",
        "position": "Shooting Guard",
        "date_of_birth": "2003-02-10"
    },
    {
        "name": "Alex Fudge",
        "team": "Los Angeles Lakers",
        "nationality": "United States",
        "position": "Small Forward",
        "date_of_birth": "2003-05-06"
    },
    {
        "name": "D'Moi Hodge",
        "team": "Los Angeles Lakers",
        "nationality": "British Virgin Islands",
        "position": "Shooting Guard",
        "date_of_birth": "1998-12-20"
    },
    {
        "name": "Jalen Hood-Schifino",
        "team": "Los Angeles Lakers",
        "nationality": "United States",
        "position": "Point Guard",
        "date_of_birth": "2003-06-19"
    },
    {
        "name": "Maxwell Lewis",
        "team": "Los Angeles Lakers",
        "nationality": "United States",
        "position": "Small Forward",
        "date_of_birth": "2002-07-27"
    },
    {
        "name": "Jarred Vanderbilt",
        "team": "Los Angeles Lakers",
        "nationality": "United States",
        "position": "Power Forward",
        "date_of_birth": "1999-04-03"
    },
    {
        "name": "Christian Wood",
        "team": "Los Angeles Lakers",
        "nationality": "United States",
        "position": "Power Forward",
        "date_of_birth": "1995-09-27"
    },
    {
        "name": "Jaxson Hayes",
        "team": "Los Angeles Lakers",
        "nationality": "United States",
        "position": "Center",
        "date_of_birth": "2000-05-23"
    },
    {
        "name": "Taurean Prince",
        "team": "Los Angeles Lakers",
        "nationality": "United States",
        "position": "Small Forward",
        "date_of_birth": "1994-03-22"
    },
    {
        "name": "Rui Hachimura",
        "team": "Los Angeles Lakers",
        "nationality": "Japan",
        "position": "Power Forward",
        "date_of_birth": "1998-02-08"
    },
    {
        "name": "Cam Reddish",
        "team": "Los Angeles Lakers",
        "nationality": "United States",
        "position": "Small Forward",
        "date_of_birth": "1999-09-01"
    },
    {
        "name": "Austin Reaves",
        "team": "Los Angeles Lakers",
        "nationality": "United States",
        "position": "Shooting Guard",
        "date_of_birth": "1998-05-29"
    },
    {
        "name": "Gabe Vincent",
        "team": "Los Angeles Lakers",
        "nationality": "Nigeria",
        "position": "Point Guard",
        "date_of_birth": "1996-06-14"
    },
    {
        "name": "Joshua Primo",
        "team": "Los Angeles Clippers",
        "nationality": "Canada",
        "position": "Shooting Guard",
        "date_of_birth": "2002-12-24"
    },
    {
        "name": "James Harden",
        "team": "Los Angeles Clippers",
        "nationality": "United States",
        "position": "Point Guard",
        "date_of_birth": "1989-08-26"
    },
    {
        "name": "Daniel Theis",
        "team": "Los Angeles Clippers",
        "nationality": "Germany",
        "position": "Center",
        "date_of_birth": "1992-04-04"
    },
    {
        "name": "Ivica Zubac",
        "team": "Los Angeles Clippers",
        "nationality": "Croatia",
        "position": "Center",
        "date_of_birth": "1997-03-18"
    },
    {
        "name": "Kawhi Leonard",
        "team": "Los Angeles Clippers",
        "nationality": "United States",
        "position": "Small Forward",
        "date_of_birth": "1991-06-29"
    },
    {
        "name": "Mason Plumlee",
        "team": "Los Angeles Clippers",
        "nationality": "United States",
        "position": "Power Forward",
        "date_of_birth": "1990-03-05"
    },
    {
        "name": "Russell Westbrook",
        "team": "Los Angeles Clippers",
        "nationality": "United States",
        "position": "Point Guard",
        "date_of_birth": "1988-11-12"
    },
    {
        "name": "Norman Powell",
        "team": "Los Angeles Clippers",
        "nationality": "United States",
        "position": "Small Forward",
        "date_of_birth": "1993-05-25"
    },
    {
        "name": "P. J. Tucker",
        "team": "Los Angeles Clippers",
        "nationality": "United States",
        "position": "Forward",
        "date_of_birth": "1985-05-05"
    },
    {
        "name": "Paul George",
        "team": "Los Angeles Clippers",
        "nationality": "United States",
        "position": "Small Forward",
        "date_of_birth": "1990-05-02"
    },
    {
        "name": "Bones Hyland",
        "team": "Los Angeles Clippers",
        "nationality": "United States",
        "position": "Point Guard",
        "date_of_birth": "2000-09-14"
    },
    {
        "name": "Amir Coffey",
        "team": "Los Angeles Clippers",
        "nationality": "United States",
        "position": "Shooting Guard",
        "date_of_birth": "1997-06-17"
    },
    {
        "name": "Terance Mann",
        "team": "Los Angeles Clippers",
        "nationality": "United States",
        "position": "Small Forward",
        "date_of_birth": "1996-10-18"
    },
    {
        "name": "Tyronn Lue",
        "team": "Los Angeles Clippers",
        "nationality": "United States",
        "position": "Manager",
        "date_of_birth": "1977-05-03"
    },
    {
        "name": "Brandon Boston Jr.",
        "team": "Los Angeles Clippers",
        "nationality": "United States",
        "position": "Shooting Guard",
        "date_of_birth": "2001-11-28"
    },
    {
        "name": "Jordan Miller",
        "team": "Los Angeles Clippers",
        "nationality": "United States",
        "position": "Small Forward",
        "date_of_birth": "2000-01-23"
    },
    {
        "name": "Xavier Moon",
        "team": "Los Angeles Clippers",
        "nationality": "United States",
        "position": "Shooting Guard",
        "date_of_birth": "1995-01-02"
    },
    {
        "name": "Kobe Brown",
        "team": "Los Angeles Clippers",
        "nationality": "United States",
        "position": "Small Forward",
        "date_of_birth": "2000-01-01"
    },
    {
        "name": "Moussa Diabaté",
        "team": "Los Angeles Clippers",
        "nationality": "France",
        "position": "Center",
        "date_of_birth": "2002-01-21"
    },
    {
        "name": "Josh Christopher",
        "team": "Utah Jazz",
        "nationality": "United States",
        "position": "Shooting Guard",
        "date_of_birth": "2001-12-08"
    },
    {
        "name": "Talen Horton-Tucker",
        "team": "Utah Jazz",
        "nationality": "United States",
        "position": "Shooting Guard",
        "date_of_birth": "2000-11-25"
    },
    {
        "name": "Will Hardy",
        "team": "Utah Jazz",
        "nationality": "United States",
        "position": "Manager",
        "date_of_birth": "1988-01-21"
    },
    {
        "name": "Danuel House Jr.",
        "team": "Utah Jazz",
        "nationality": "United States",
        "position": "Small Forward",
        "date_of_birth": "1993-06-07"

    }

    
    // Add more player objects as required
];
    const dummyGames = [
    { "date": "2022-10-05", "home_team": "Chicago Bulls", "away_team": "New Orleans Pelicans", "home_score": 125, "away_score": 129 },
    { "date": "2022-10-05", "home_team": "Portland Trail Blazers", "away_team": "Utah Jazz", "home_score": 101, "away_score": 118 },
    { "date": "2022-10-02", "home_team": "Houston Rockets", "away_team": "San Antonio Spurs", "home_score": 134, "away_score": 96 },
    { "date": "2022-10-02", "home_team": "Boston Celtics", "away_team": "Charlotte Hornets", "home_score": 134, "away_score": 93 },
    { "date": "2022-10-03", "home_team": "Brooklyn Nets", "away_team": "Philadelphia 76ers", "home_score": 108, "away_score": 127 },
    { "date": "2022-10-04", "home_team": "Portland Trail Blazers", "away_team": "Los Angeles Clippers", "home_score": 102, "away_score": 97 },
    { "date": "2022-10-04", "home_team": "Los Angeles Lakers", "away_team": "Sacramento Kings", "home_score": 75, "away_score": 105 },
    { "date": "2022-10-04", "home_team": "Miami Heat", "away_team": "Minnesota Timberwolves", "home_score": 111, "away_score": 121 },
    { "date": "2022-10-04", "home_team": "Memphis Grizzlies", "away_team": "Orlando Magic", "home_score": 109, "away_score": 97 },
    { "date": "2022-10-04", "home_team": "Denver Nuggets", "away_team": "Oklahoma City Thunder", "home_score": 101, "away_score": 112 }
    // Add more dummy game objects as needed
];

export const fetchGames = async () => {
    const correlationId = generateCorrelationId();
    const context = 'FETCH_GAMES';
    
    Logger.info(context, 'Starting games fetch', { correlationId });

    // Check cache first
    if (apiCache.games && isCacheValid('games')) {
        Logger.info(context, 'Using cached games data', {
            correlationId,
            cachedCount: apiCache.games.length,
            cacheAge: Date.now() - apiCache.lastFetch['games']
        });
        return apiCache.games;
    }

    // Prevent concurrent fetches
    if (apiCache.isFetchingGames) {
        Logger.info(context, 'Games fetch already in progress, waiting', { correlationId });
        let waitCount = 0;
        // Wait for the current fetch to complete
        while (apiCache.isFetchingGames) {
            await new Promise(resolve => setTimeout(resolve, 100));
            waitCount++;
            if (waitCount > 100) { // 10 second timeout
                Logger.error(context, 'Timeout waiting for concurrent fetch', null, { 
                    correlationId, 
                    waitTime: waitCount * 100 
                });
                break;
            }
        }
        // Check cache again after waiting
        if (apiCache.games && isCacheValid('games')) {
            Logger.info(context, 'Using cached games data after waiting', {
                correlationId,
                waitTime: waitCount * 100,
                cachedCount: apiCache.games.length
            });
            return apiCache.games;
        }
    }

    apiCache.isFetchingGames = true;
    Logger.info(context, 'Starting fresh games fetch', { correlationId });

    try {
        const currentSeasonEndpoint = `${API_BASE_URL}/games?season=2025-2026`;
        const lastSeasonEndpoint = `${API_BASE_URL}/games?season=2024-2025`;
        
        Logger.info(context, 'Making parallel season API calls', {
            correlationId,
            endpoints: {
                currentSeason: currentSeasonEndpoint,
                lastSeason: lastSeasonEndpoint
            }
        });

        const requestStart = Date.now();
        
        // Fetch both current and last season in parallel
        const [currentSeasonResponse, lastSeasonResponse] = await Promise.allSettled([
            axios.get(currentSeasonEndpoint),
            axios.get(lastSeasonEndpoint)
        ]);

        const requestDuration = Date.now() - requestStart;
        Logger.info(context, 'Season API calls completed', {
            correlationId,
            duration: `${requestDuration}ms`,
            currentSeasonStatus: currentSeasonResponse.status,
            lastSeasonStatus: lastSeasonResponse.status
        });

        let allGames = [];
        const seenGames = new Set(); // Track unique games to prevent duplicates

        // Helper function to create a unique key for a game
        const getGameKey = (game) => {
            return `${game.date}_${game.home_team}_${game.away_team}_${game.league}`;
        };

        // Helper function to add games with deduplication
        const addGamesWithDedup = (games, season) => {
            let added = 0;
            let skipped = 0;
            const duplicates = [];
            
            games.forEach(game => {
                const gameKey = getGameKey(game);
                if (!seenGames.has(gameKey)) {
                    seenGames.add(gameKey);
                    allGames.push({
                        ...game,
                        date: moment(game.date).format('YYYY-MM-DD'),
                        league: game.league,
                        season: season
                    });
                    added++;
                } else {
                    duplicates.push(gameKey);
                    skipped++;
                }
            });
            
            Logger.info(context, `Processed ${season} games`, {
                correlationId,
                season,
                totalGames: games.length,
                added,
                skipped,
                duplicates: duplicates.slice(0, 5) // Show first 5 duplicates
            });
        };

        // Process current season games
        if (currentSeasonResponse.status === 'fulfilled' && Array.isArray(currentSeasonResponse.value.data)) {
            Logger.info(context, 'Processing current season games', {
                correlationId,
                season: '2025-2026',
                count: currentSeasonResponse.value.data.length,
                sampleGames: currentSeasonResponse.value.data.slice(0, 3).map(g => ({
                    date: g.date,
                    match: `${g.home_team} vs ${g.away_team}`,
                    league: g.league
                }))
            });
            addGamesWithDedup(currentSeasonResponse.value.data, '2025-2026');
        } else {
            Logger.error(context, 'Current season games failed to load', currentSeasonResponse.reason, {
                correlationId,
                season: '2025-2026',
                status: currentSeasonResponse.status
            });
        }

        // Process last season games
        if (lastSeasonResponse.status === 'fulfilled' && Array.isArray(lastSeasonResponse.value.data)) {
            Logger.info(context, 'Processing last season games', {
                correlationId,
                season: '2024-2025',
                count: lastSeasonResponse.value.data.length,
                sampleGames: lastSeasonResponse.value.data.slice(0, 3).map(g => ({
                    date: g.date,
                    match: `${g.home_team} vs ${g.away_team}`,
                    league: g.league
                }))
            });
            addGamesWithDedup(lastSeasonResponse.value.data, '2024-2025');
        } else {
            Logger.error(context, 'Last season games failed to load', lastSeasonResponse.reason, {
                correlationId,
                season: '2024-2025',
                status: lastSeasonResponse.status
            });
        }

        if (allGames.length > 0) {
            // Sort all games by date (most recent first)
            allGames.sort((a, b) => new Date(b.date) - new Date(a.date));
            
            // Cache the result
            apiCache.games = allGames;
            apiCache.lastFetch['games'] = Date.now();
            
            Logger.info(context, 'Games fetch completed successfully', {
                correlationId,
                totalGames: allGames.length,
                breakdown: {
                    '2025-2026': allGames.filter(g => g.season === '2025-2026').length,
                    '2024-2025': allGames.filter(g => g.season === '2024-2025').length,
                    leagueBreakdown: {
                        NBA: allGames.filter(g => g.league === 'NBA').length,
                        PremierLeague: allGames.filter(g => g.league === 'PremierLeague').length
                    }
                },
                duration: `${Date.now() - requestStart}ms`
            });
            
            return allGames;
        } else {
            Logger.error(context, 'No games loaded from either season', null, {
                correlationId,
                currentSeasonStatus: currentSeasonResponse.status,
                lastSeasonStatus: lastSeasonResponse.status
            });
            throw new Error('No games available from either season');
        }
    } catch (error) {
        Logger.error(context, 'Critical error in games fetch', error, {
            correlationId,
            errorType: error.constructor.name,
            apiBaseUrl: API_BASE_URL
        });
        
        Logger.warn(context, 'Falling back to dummy games', {
            correlationId,
            dummyGamesCount: dummyGames.length
        });
        return dummyGames;
    } finally {
        apiCache.isFetchingGames = false;
        Logger.debug(context, 'Games fetch lock released', { correlationId });
    }
};

// Function to fetch players with fallback to dummy data
export const fetchPlayers = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/players/api/players/`);
        if (Array.isArray(response.data) && response.data.length) {
            return response.data;
        } else {
            throw new Error('Invalid format or empty data');
        }
    } catch (error) {
        console.error('Error fetching players or API is down, using dummy data:', error);
        if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            console.error(error.response.data);
            console.error(error.response.status);
            console.error(error.response.headers);
        } else if (error.request) {
            // The request was made but no response was received
            console.error(error.request);
        } else {
            // Something happened in setting up the request that triggered an Error
            console.error('Error', error.message);
        }
        return dummyPlayers; // Fallback to dummy data
    }
};


export const fetchPredictions = async (startDate, endDate) => {
    const correlationId = generateCorrelationId();
    const context = 'FETCH_PREDICTIONS';
    
    Logger.info(context, 'Starting predictions fetch', {
        correlationId,
        startDate,
        endDate,
        dateRange: `${startDate} to ${endDate}`
    });

    // Check cache first
    const cacheKey = `predictions_${startDate}_${endDate}`;
    if (apiCache.predictions && isCacheValid(cacheKey)) {
        Logger.info(context, 'Using cached predictions data', {
            correlationId,
            cacheKey,
            cachedCount: apiCache.predictions.length,
            cacheAge: Date.now() - apiCache.lastFetch[cacheKey]
        });
        return apiCache.predictions;
    }

    Logger.debug(context, 'Cache miss - fetching fresh predictions', {
        correlationId,
        cacheKey,
        cacheExists: !!apiCache.predictions,
        cacheValid: isCacheValid(cacheKey)
    });

    try {
        // Log API endpoints being called - Both leagues use Go server with same SQL database
        const plEndpoint = `${API_BASE_URL}/predictions?start_date=${startDate}&end_date=${endDate}&league=PremierLeague`;
        const nbaEndpoint = `${API_BASE_URL}/predictions?start_date=${startDate}&end_date=${endDate}&league=NBA`;
        
        Logger.info(context, 'Making parallel API calls', {
            correlationId,
            endpoints: {
                premierLeague: plEndpoint,
                nba: nbaEndpoint
            }
        });

        const requestStart = Date.now();
        
        // Fetch football and NBA predictions for both seasons in parallel
        const [footballResponse, nbaResponse] = await Promise.allSettled([
            axios.get(plEndpoint),
            axios.get(nbaEndpoint)
        ]);
        
        const requestDuration = Date.now() - requestStart;
        Logger.info(context, 'API calls completed', {
            correlationId,
            duration: `${requestDuration}ms`,
            plStatus: footballResponse.status,
            nbaStatus: nbaResponse.status
        });
        
        let allPredictions = [];
        
        // Process Premier League predictions from Go server
        if (footballResponse.status === 'fulfilled') {
            const plData = footballResponse.value.data.predictions || footballResponse.value.data;
            Logger.info(context, 'Processing Premier League predictions', {
                correlationId,
                rawDataType: Array.isArray(plData) ? 'array' : typeof plData,
                count: Array.isArray(plData) ? plData.length : 0,
                responseStructure: {
                    hasData: !!footballResponse.value.data,
                    hasPredictions: !!footballResponse.value.data.predictions,
                    dataKeys: Object.keys(footballResponse.value.data || {})
                }
            });
            
            if (Array.isArray(plData)) {
                const formattedPLPredictions = plData.map(prediction => {
                    // Handle empty or invalid prediction dates
                    let predictionDate = prediction.prediction_date || prediction.predictionDate || prediction.PredictionDate || prediction.prediction_timestamp;
                    
                    // If the date is empty or invalid, use current timestamp as fallback
                    if (!predictionDate || predictionDate === '') {
                        predictionDate = new Date().toISOString();
                        Logger.warn(context, 'Using fallback prediction date', {
                            correlationId,
                            gameKey: `${prediction.date}_${prediction.home_team}_${prediction.away_team}`,
                            originalDate: predictionDate,
                            fallbackDate: predictionDate
                        });
                    }
                    
                    return {
                        ...prediction,
                        date: moment(prediction.date).format('YYYY-MM-DD'),
                        predictionDate: moment(predictionDate).format('YYYY-MM-DD HH:mm:ss'),
                        league: "PremierLeague"
                    };
                });
                allPredictions = allPredictions.concat(formattedPLPredictions);
                
                Logger.info(context, 'Processed Premier League predictions', {
                    correlationId,
                    processed: formattedPLPredictions.length,
                    sampleGames: formattedPLPredictions.slice(0, 3).map(p => ({
                        date: p.date,
                        match: `${p.home_team} vs ${p.away_team}`,
                        homeProb: p.home_win_prob
                    }))
                });
            } else {
                Logger.warn(context, 'Premier League data is not an array', {
                    correlationId,
                    dataType: typeof plData,
                    data: plData
                });
            }
        } else {
            Logger.error(context, 'Premier League predictions failed', footballResponse.reason, {
                correlationId,
                endpoint: plEndpoint,
                status: footballResponse.status
            });
        }
        
        // Process NBA predictions from Go server
        if (nbaResponse.status === 'fulfilled') {
            const nbaData = nbaResponse.value.data.predictions || nbaResponse.value.data;
            Logger.info(context, 'Processing NBA predictions', {
                correlationId,
                rawDataType: Array.isArray(nbaData) ? 'array' : typeof nbaData,
                count: Array.isArray(nbaData) ? nbaData.length : 0,
                responseStructure: {
                    hasData: !!nbaResponse.value.data,
                    hasPredictions: !!nbaResponse.value.data.predictions,
                    dataKeys: Object.keys(nbaResponse.value.data || {})
                }
            });
            
            if (Array.isArray(nbaData)) {
                const formattedNBAPredictions = nbaData.map(prediction => {
                    // Normalize NBA team names to match games database
                    const normalizeNBATeamName = (teamName) => {
                        const teamMappings = {
                            'LA Clippers': 'Los Angeles Clippers',
                            'LA Lakers': 'Los Angeles Lakers'
                        };
                        return teamMappings[teamName] || teamName;
                    };
                    
                    // Handle empty or invalid prediction dates
                    let predictionDate = prediction.PredictionDate || prediction.prediction_date || prediction.predictionDate || prediction.prediction_timestamp;
                    
                    // If the date is empty or invalid, use current timestamp as fallback
                    if (!predictionDate || predictionDate === '') {
                        predictionDate = new Date().toISOString();
                        Logger.warn(context, 'Using fallback NBA prediction date', {
                            correlationId,
                            gameKey: `${prediction.Date || prediction.date}_${prediction.home_team}_${prediction.away_team}`,
                            originalDate: predictionDate,
                            fallbackDate: predictionDate
                        });
                    }
                    
                    return {
                        ...prediction,
                        date: moment(prediction.Date || prediction.date).format('YYYY-MM-DD'),
                        predictionDate: moment(predictionDate).format('YYYY-MM-DD HH:mm:ss'),
                        league: prediction.League || prediction.league,
                        home_team: normalizeNBATeamName(prediction.home_team),
                        away_team: normalizeNBATeamName(prediction.away_team)
                    };
                });
                allPredictions = allPredictions.concat(formattedNBAPredictions);
                
                Logger.info(context, 'Processed NBA predictions', {
                    correlationId,
                    processed: formattedNBAPredictions.length,
                    sampleGames: formattedNBAPredictions.slice(0, 3).map(p => ({
                        date: p.date,
                        match: `${p.home_team} vs ${p.away_team}`,
                        homeProb: p.home_win_prob
                    }))
                });
            } else {
                Logger.warn(context, 'NBA data is not an array', {
                    correlationId,
                    dataType: typeof nbaData,
                    data: nbaData
                });
            }
        } else {
            Logger.error(context, 'NBA predictions failed', nbaResponse.reason, {
                correlationId,
                endpoint: nbaEndpoint,
                status: nbaResponse.status
            });
        }
        
        Logger.info(context, 'Predictions fetch completed successfully', {
            correlationId,
            totalPredictions: allPredictions.length,
            breakdown: {
                premierLeague: allPredictions.filter(p => p.league === 'PremierLeague').length,
                nba: allPredictions.filter(p => p.league === 'NBA').length
            },
            duration: `${Date.now() - requestStart}ms`
        });
        
        // Cache the result
        apiCache.predictions = allPredictions;
        apiCache.lastFetch[cacheKey] = Date.now();
        
        Logger.debug(context, 'Cached predictions for future requests', {
            correlationId,
            cacheKey,
            cachedCount: allPredictions.length
        });
        
        return allPredictions;
        
    } catch (error) {
        Logger.error(context, 'Critical error in predictions fetch', error, {
            correlationId,
            startDate,
            endDate,
            errorType: error.constructor.name
        });
        
        // Return empty array as fallback
        return [];
    }
};

// Fetch a specific NBA prediction from Go server
export const fetchNBAPrediction = async (homeTeam, awayTeam, date) => {
    try {
        console.log(`Fetching NBA prediction: ${homeTeam} vs ${awayTeam} on ${date}`);
        
        const response = await axios.get(`${API_BASE_URL}/predictions`, {
            params: {
                league: 'NBA',
                home_team: homeTeam,
                away_team: awayTeam,
                date: date
            }
        });
        
        if (response.data) {
            console.log('NBA prediction fetched successfully from Go server');
            return {
                ...response.data,
                date: moment(response.data.Date || response.data.date).format('YYYY-MM-DD'),
                predictionDate: moment(response.data.PredictionDate || response.data.prediction_date).format('YYYY-MM-DD HH:mm:ss'),
                league: response.data.League || response.data.league
            };
        }
        
        return null;
    } catch (error) {
        console.error('Error fetching NBA prediction from Go server:', error.message);
        if (error.response && error.response.status === 404) {
            console.log('NBA prediction not found in Go server');
        }
        return null;
    }
};


export const fetchEventStats = async (eventId) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/event-stats?id=${eventId}`);
        if (response.status === 200 && response.data) {
            return response.data; // Return the event stats
        } else {
            throw new Error('Unexpected response format');
        }
    } catch (error) {
        console.error(`Error fetching event stats for event ID ${eventId}:`, error.message);
        if (error.response) {
            console.error(error.response.data);
            console.error(error.response.status);
            console.error(error.response.headers);
        } else if (error.request) {
            console.error(error.request);
        } else {
            console.error('Error', error.message);
        }
        throw error; // Propagate the error for further handling
    }
};

export const fetchEventLineup = async (eventId) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/event-lineup?id=${eventId}`);
        if (response.status === 200 && response.data) {
            return response.data; // Return the event lineup
        } else {
            throw new Error('Unexpected response format');
        }
    } catch (error) {
        console.error(`Error fetching event lineup for event ID ${eventId}:`, error.message);
        if (error.response) {
            console.error(error.response.data);
            console.error(error.response.status);
            console.error(error.response.headers);
        } else if (error.request) {
            console.error(error.request);
        } else {
            console.error('Error', error.message);
        }
        throw error; // Propagate the error for further handling
    }
};

export const fetchBoxScore = async (homeTeam, awayTeam, date = null) => {
    try {
        let url = `${NBA_API_BASE_URL}/box-score?home_team=${encodeURIComponent(homeTeam)}&away_team=${encodeURIComponent(awayTeam)}`;
        if (date) {
            url += `&date=${date}`;
        }
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching box score:', error.message);
        if (error.response) {
            console.error(error.response.data);
            console.error(error.response.status);
            console.error(error.response.headers);
        } else if (error.request) {
            console.error(error.request);
        } else {
            console.error('Error', error.message);
        }
        throw error; // Re-throw to let the component handle the error
    }
};

// Fetch Premier League standings with Elo ratings and probabilities
export const fetchPLStandings = async (retries = 2) => {
    const cacheKey = 'pl_standings';

    // Check cache first
    if (apiCache[cacheKey] && isCacheValid(cacheKey)) {
        console.log('Using cached Premier League standings data');
        return apiCache[cacheKey];
    }

    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            const response = await axios.get(`${NBA_API_BASE_URL}/api/v1/standings/pl`);

            if (Array.isArray(response.data)) {
                // Cache the result
                apiCache[cacheKey] = response.data;
                apiCache.lastFetch[cacheKey] = Date.now();

                return response.data;
            } else {
                throw new Error('Invalid standings data format');
            }
        } catch (error) {
            if (attempt === retries) {
                // Final attempt failed, check for offline/dev mode data
                console.warn('API call failed, no offline data available for Premier League standings');
                // Unlike NBA, we don't have offline data for PL
                return [];
            }

            console.error('Error fetching Premier League standings:', error.message);
            throw error; // Let component handle the error
        }
    }
};

// Fetch NBA team standings with Elo ratings and playoff probabilities
export const fetchNBAStandings = async (retries = 2) => {
    const cacheKey = 'nba_standings';

    // Check cache first
    if (apiCache[cacheKey] && isCacheValid(cacheKey)) {
        console.log('Using cached NBA standings data');
        return apiCache[cacheKey];
    }

    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            const response = await axios.get(`${NBA_API_BASE_URL}/api/v1/standings/nba`);

            if (Array.isArray(response.data)) {
                // Cache the result
                apiCache[cacheKey] = response.data;
                apiCache.lastFetch[cacheKey] = Date.now();

                return response.data;
            } else {
                throw new Error('Invalid standings data format');
            }
        } catch (error) {
            if (attempt < retries) {
                // Small backoff before retrying
                await new Promise(res => setTimeout(res, 500 * (attempt + 1)));
                continue;
            }

            console.error('Error fetching NBA standings:', error.message);
            throw error; // Let component handle the error
        }
    }
};

// ============================================================================
// LIVE SCORES API FUNCTIONS
// ============================================================================

// Fetch currently live games
export const fetchLiveScores = async (leagueId = null, sport = null) => {
    const cacheKey = `live_scores${leagueId ? `:league:${leagueId}` : ''}${sport ? `:sport:${sport}` : ''}`;
    
    // Check cache first
    if (apiCache.liveScores && isCacheValid(cacheKey)) {
        console.log('Using cached live scores data');
        return apiCache.liveScores;
    }

    try {
        let url = `${NBA_API_BASE_URL}/api/v1/live-scores`;
        const params = new URLSearchParams();
        
        if (leagueId) {
            params.append('league_id', leagueId);
        }
        if (sport) {
            params.append('sport', sport);
        }
        
        if (params.toString()) {
            url += `?${params.toString()}`;
        }

        const response = await axios.get(url);
        
        if (Array.isArray(response.data)) {
            // Cache the result
            apiCache.liveScores = response.data;
            apiCache.lastFetch[cacheKey] = Date.now();
            
            console.log(`Fetched ${response.data.length} live games`);
            return response.data;
        } else {
            throw new Error('Invalid live scores data format');
        }
    } catch (error) {
        console.error('Error fetching live scores:', error.message);
        throw error;
    }
};

// Fetch real-time updates for a specific game
export const fetchGameUpdates = async (eventId) => {
    const cacheKey = `live_game:${eventId}`;
    
    // Check cache first
    if (apiCache.liveGames[eventId] && isCacheValid(cacheKey)) {
        console.log(`Using cached game updates for ${eventId}`);
        return apiCache.liveGames[eventId];
    }

    try {
        const response = await axios.get(`${NBA_API_BASE_URL}/api/v1/live-scores/game/${eventId}`);
        
        if (response.data) {
            // Cache the result
            apiCache.liveGames[eventId] = response.data;
            apiCache.lastFetch[cacheKey] = Date.now();
            
            console.log(`Updated game data for ${eventId} (live: ${response.data.is_live})`);
            return response.data;
        } else {
            throw new Error('Invalid game update data format');
        }
    } catch (error) {
        console.error(`Error fetching game updates for ${eventId}:`, error.message);
        throw error;
    }
};

// Fetch season games with live status detection
export const fetchSeasonGamesWithLiveStatus = async (leagueId, season) => {
    const cacheKey = `season_games:${leagueId}:${season}`;
    
    // Check cache first
    if (apiCache.games && isCacheValid(cacheKey)) {
        console.log('Using cached season games data');
        return apiCache.games;
    }

    try {
        const response = await axios.get(`${NBA_API_BASE_URL}/api/v1/live-scores/season/${leagueId}/${season}`);
        
        if (response.data && response.data.games) {
            // Cache the result
            apiCache.games = response.data;
            apiCache.lastFetch[cacheKey] = Date.now();
            
            console.log(`Fetched ${response.data.total_games} total games, ${response.data.live_games} live for ${leagueId} ${season}`);
            return response.data;
        } else {
            throw new Error('Invalid season games data format');
        }
    } catch (error) {
        console.error(`Error fetching season games for ${leagueId} ${season}:`, error.message);
        throw error;
    }
};

// Get live scores service status
export const fetchLiveScoresStatus = async () => {
    try {
        const response = await axios.get(`${NBA_API_BASE_URL}/api/v1/live-scores/status`);
        return response.data;
    } catch (error) {
        console.error('Error fetching live scores status:', error.message);
        throw error;
    }
};

// Function to trigger backend cache warming (games only)
// Note: This should only be used by admin tools, not by regular users
export const triggerCacheWarming = async () => {
    try {
        console.log('Triggering backend games cache warming...');
        const response = await axios.post(`${API_BASE_URL}/cache/warm`);
        console.log('Games cache warming triggered:', response.data);
        return response.data;
    } catch (error) {
        console.error('Failed to trigger games cache warming:', error.message);
        // Don't throw - this is a background operation
    }
};

// Function to warm cache for specific season
export const warmSeasonCache = async (season) => {
    try {
        console.log(`Warming cache for season ${season}...`);
        const response = await axios.post(`${API_BASE_URL}/cache/warm/season?season=${season}`);
        console.log(`Cache warming for season ${season}:`, response.data);
        return response.data;
    } catch (error) {
        console.error(`Failed to warm cache for season ${season}:`, error.message);
        // Don't throw - this is a background operation
    }
};

// Function to check cache status
export const checkCacheStatus = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/cache/status`);
        console.log('Cache status:', response.data);
        return response.data;
    } catch (error) {
        console.error('Failed to check cache status:', error.message);
        return null;
    }
};

// Function to check if games cache is stale
export const isGamesCacheStale = () => {
    if (!apiCache.games || !apiCache.lastFetch['games']) {
        return true; // No cache, consider it stale
    }
    
    const now = Date.now();
    const lastFetch = apiCache.lastFetch['games'];
    const cacheAge = now - lastFetch;
    const cacheAgeMinutes = cacheAge / (1000 * 60);
    
    // Less aggressive cache invalidation - consider stale if older than 30 minutes
    // This reduces unnecessary cache warming while still ensuring fresh data
    return cacheAgeMinutes > 30; // Increased from 15 minutes to 30 minutes
};

// =============================================================================
// RODMAN AI API FUNCTIONS
// =============================================================================

// Chat with Rodman AI
export const chatWithRodman = async (query, gameContext = null, isPreset = false, queryType = 'general') => {
    try {
        const payload = { query };
        
        // Add preset flag for backend enrichment
        if (isPreset) {
            payload.is_preset = true;
        }
        
        // Add query type for enhanced enrichment
        if (queryType && queryType !== 'general') {
            payload.query_type = queryType;
        }
        
        // Add game context if provided
        if (gameContext) {
            // If it's a rich context object (game, standings, or other complex context), pass as context
            if (gameContext.home_team || gameContext.away_team || gameContext.standings || gameContext.league || Object.keys(gameContext).length > 4) {
                payload.context = gameContext;
            } else {
                // Legacy simple context
                if (gameContext.home_team) payload.home_team = gameContext.home_team;
                if (gameContext.away_team) payload.away_team = gameContext.away_team;
                if (gameContext.date) payload.date = gameContext.date;
                if (gameContext.game_id) payload.game_id = gameContext.game_id;
            }
        }
        
        // Enhanced debugging for investigation
        console.group('🔌 API - Rodman Chat Request');
        console.log('Payload being sent:', JSON.stringify(payload, null, 2));
        console.log('Context keys:', payload.context ? Object.keys(payload.context) : 'No context');
        if (payload.context?.standings) {
            console.log('Standings teams:', payload.context.standings.map(t => t.teamFullName || t.team));
        }
        console.log('API URL:', `${API_BASE_URL}/api/v1/rodman/chat`);
        console.groupEnd();
        
        const response = await axios.post(`${API_BASE_URL}/api/v1/rodman/chat`, payload);
        console.log('Rodman response:', response.data);
        
        return response.data;
    } catch (error) {
        console.error('Error chatting with Rodman:', error);
        throw error;
    }
};

// Get Rodman's daily picks
export const getRodmanPicks = async (date = null, limit = 5) => {
    try {
        let url = `${NBA_API_BASE_URL}/api/v1/rodman/picks?limit=${limit}`;
        if (date) {
            url += `&date=${date}`;
        }
        
        console.log('Getting Rodman picks:', url);
        
        const response = await axios.get(url);
        console.log('Rodman picks:', response.data);
        
        return response.data;
    } catch (error) {
        console.error('Error getting Rodman picks:', error);
        throw error;
    }
};

// Get Rodman's weekly report
export const getRodmanWeeklyReport = async () => {
    try {
        console.log('Getting Rodman weekly report...');
        
        const response = await axios.get(`${NBA_API_BASE_URL}/api/v1/rodman/weekly-report`);
        console.log('Rodman weekly report:', response.data);
        
        return response.data;
    } catch (error) {
        console.error('Error getting Rodman weekly report:', error);
        throw error;
    }
};

// Get Rodman usage statistics
export const getRodmanStats = async () => {
    try {
        console.log('Getting Rodman stats...');
        
        const response = await axios.get(`${API_BASE_URL}/api/v1/rodman/stats`);
        console.log('Rodman stats:', response.data);
        
        return response.data;
    } catch (error) {
        console.error('Error getting Rodman stats:', error);
        throw error;
    }
};

// =============================================================================
// ENHANCED RODMAN API FUNCTIONS FOR PHASE 2
// =============================================================================

// Context-aware Rodman queries with enhanced data passing
export const chatWithRodmanEnhanced = async (query, contextType, contextData, isPreset = true) => {
    try {
        const payload = { 
            query,
            is_preset: isPreset 
        };
        
        // Add context based on type
        switch (contextType) {
            case 'game':
                if (contextData.game) payload.context = contextData.game;
                if (contextData.prediction) payload.prediction = contextData.prediction;
                if (contextData.standings) payload.standings = contextData.standings;
                break;
                
            case 'team':
                if (contextData.team) payload.team_context = contextData.team;
                if (contextData.league) payload.league = contextData.league;
                break;
                
            case 'standings':
                if (contextData.league) payload.league = contextData.league;
                if (contextData.focusTeam) payload.focus_team = contextData.focusTeam;
                break;
                
            default:
                // General context
                if (contextData) payload.context = contextData;
        }
        
        console.log('Enhanced Rodman chat:', { query, contextType, payload });
        
        const response = await axios.post(`${NBA_API_BASE_URL}/api/v1/rodman/chat`, payload);
        console.log('Enhanced Rodman response:', response.data);
        
        return response.data;
    } catch (error) {
        console.error('Error with enhanced Rodman chat:', error);
        throw error;
    }
};

// Quick preset queries for specific use cases
export const getRodmanGameAnalysis = async (gameData, analysisType = 'breakdown') => {
    try {
        const queries = {
            breakdown: "Break down the key factors for this game",
            prediction: "Based on recent form and matchup history, who wins this game?",
            factors: "What are the key factors that will decide this game?",
            upset: "Is this game a potential upset based on the odds?"
        };
        
        const query = queries[analysisType] || queries.breakdown;
        
        return await chatWithRodmanEnhanced(query, 'game', { game: gameData }, true);
    } catch (error) {
        console.error('Error getting Rodman game analysis:', error);
        throw error;
    }
};

// Team-specific analysis
export const getRodmanTeamOutlook = async (teamName, league, analysisType = 'outlook') => {
    try {
        const queries = {
            outlook: `What's ${teamName}'s realistic ceiling this season based on current form?`,
            ceiling: `Based on current performance, what's ${teamName}'s season ceiling?`,
            form: `How is ${teamName} trending based on recent performance?`,
            playoff: `What are ${teamName}'s playoff chances based on current standings?`
        };
        
        const query = queries[analysisType] || queries.outlook;
        
        return await chatWithRodmanEnhanced(query, 'team', { team: teamName, league }, true);
    } catch (error) {
        console.error('Error getting Rodman team outlook:', error);
        throw error;
    }
};

// Standings/league-wide insights
export const getRodmanStandingsInsight = async (league, focusArea = 'trends') => {
    try {
        const queries = {
            trends: "What are the biggest league trends and surprises this season?",
            movers: "Based on current power rankings and Elo movements, what teams should we watch?",
            form: "Which teams are trending up/down based on recent performance?",
            playoff: "How do current standings affect playoff probabilities?",
            surprises: "Which teams are the biggest surprises so far this season?"
        };
        
        const query = queries[focusArea] || queries.trends;
        
        return await chatWithRodmanEnhanced(query, 'standings', { league, focusArea }, true);
    } catch (error) {
        console.error('Error getting Rodman standings insight:', error);
        throw error;
    }
};

// Get contextual quick actions based on page type
export const getRodmanQuickActions = (pageType, contextData = {}) => {
    const actionSets = {
        game_pregame: [
            {
                query: "Break down the key factors for this game",
                label: "Key Factors",
                icon: "🎯",
                contextType: "game"
            },
            {
                query: "Based on recent form and matchup history, who wins this game?",
                label: "Game Prediction", 
                icon: "🔮",
                contextType: "game"
            }
        ],
        
        game_postgame: [
            {
                query: "Break down what happened in this game - was this expected?",
                label: "Game Analysis",
                icon: "📊", 
                contextType: "game"
            },
            {
                query: "How does this result impact the teams' season outlook?",
                label: "Impact Analysis",
                icon: "📈",
                contextType: "game"
            }
        ],
        
        standings: [
            {
                query: "What are the biggest league trends and surprises this season?",
                label: "League Trends",
                icon: "📈",
                contextType: "standings"
            },
            {
                query: "Based on current power rankings and Elo movements, what teams should we watch?",
                label: "Power Movers",
                icon: "📊",
                contextType: "standings"
            },
            {
                query: "Looking at your recent prediction accuracy, which teams are you most confident about?",
                label: "Confidence Plays",
                icon: "🎯",
                contextType: "standings"
            }
        ],
        
        team: [
            {
                query: "What's this team's realistic ceiling this season based on current form?",
                label: "Season Outlook",
                icon: "🎯",
                contextType: "team"
            },
            {
                query: "How is this team trending based on recent performance?",
                label: "Form Check",
                icon: "📊",
                contextType: "team"
            }
        ]
    };
    
    return actionSets[pageType] || actionSets.standings;
};


